import React, { useState } from 'react';
import { notify } from '@lib/bugsnag';
import { NotifiableError } from '@bugsnag/core';
import Button from '@components/Button';
import Card from '@components/Card';
import Icon from '@components/Icon';
import IconButton from '@components/IconButton';
import StackView from '@components/StackView';
import ThreeDotLoader from '@components/ThreeDotLoader';
import WalletIcon from '@components/WalletIcon';
import useGenericPopup from '@hooks/useGenericPopup';
import useTransferAssetFlowContext from '@hooks/useTransferAssetFlowContext';
import useWallets from '@hooks/useWallets';
import {
  BrowserExtensionNotInstalledError,
  UserRejectedWalletConnectionRequestError,
} from '@errors';
import { WalletType } from '@types';
import { isWalletSupportedByNetwork } from '@lib/utils';
import staticConfig from '@config/static-config';
import useUserAgent, { UserAgent } from '@hooks/useUserAgent';
import useNetworkEnv from '@hooks/useNetworkEnv';
import * as ga from '@lib/ga';

const ConnectWalletPopup = () => {
  const [connectingWallet, setConnectingWallet] = useState<WalletType | null>(
    null
  );
  const { wallets, connect } = useWallets();
  const userAgent = useUserAgent();
  const { showPopup } = useGenericPopup();
  const { onHideWalletConnection, state } = useTransferAssetFlowContext();
  const [isTestnet] = useNetworkEnv();

  const filterWalletType = (walletType: WalletType) => {
    // Hide Keplr wallet on mobile or tablet
    if (userAgent !== UserAgent.desktop && walletType === WalletType.keplr) {
      return false;
    }

    return true;
  };

  const connectWalletGaEvent = (walletType: WalletType) => {
    ga.event({
      action: 'wallet',
      params: {
        hitType: 'event',
        eventCategory: 'Wallet',
        eventAction: 'connect',
        eventLabel: walletType,
      },
    });
  };

  const handleConnectButtonClick = (walletType: WalletType) => async () => {
    try {
      setConnectingWallet(walletType);
      if (!state.hasSwitchedNetwork && wallets.length === 0) {
        const isFromNetworkSupported = isWalletSupportedByNetwork(
          walletType,
          state.fromNetwork,
          isTestnet
        );
        await connect(
          walletType,
          isFromNetworkSupported
            ? state.fromNetwork
            : staticConfig.walletSupportedNetworks[
                isTestnet ? 'testnet' : 'mainnet'
              ][walletType][0]
        );
      } else {
        await connect(
          walletType,
          wallets.length === 0 ? state.fromNetwork : state.toNetwork
        );
      }
    } catch (error) {
      if (error instanceof BrowserExtensionNotInstalledError && userAgent) {
        const downloadUrl =
          staticConfig.walletDownloadUrls[error.walletType]?.[userAgent] || '';
        switch (error.walletType) {
          case WalletType.metamask:
            window?.open(downloadUrl, '_blank')?.focus();
            break;
          case WalletType.keplr: {
            window?.open(downloadUrl, '_blank')?.focus();
            break;
          }
          default:
            break;
        }
        return;
      }

      if (error instanceof UserRejectedWalletConnectionRequestError) {
        return;
      }

      notify(error as NotifiableError);
      showPopup();
    } finally {
      setConnectingWallet(null);
      onHideWalletConnection();
      connectWalletGaEvent(walletType);
    }
  };

  const renderWalletButton = (walletType: WalletType) => {
    let disabled = false;

    if (wallets.length === 0 && state.hasSwitchedNetwork) {
      disabled =
        !isWalletSupportedByNetwork(walletType, state.fromNetwork, isTestnet) ||
        wallets.some(({ type }) => type === walletType);
    } else if (wallets.length > 0) {
      disabled =
        !isWalletSupportedByNetwork(walletType, state.toNetwork, isTestnet) ||
        wallets.some(({ type }) => type === walletType);
    }

    return (
      <Button
        className="mx-auto"
        variant="withLogo"
        logo={
          <WalletIcon
            className="text-iconLarge"
            variant={walletType}
            idPrefix="connect-wallet"
          />
        }
        key={walletType}
        onClick={handleConnectButtonClick(walletType)}
        disabled={disabled}
      >
        {walletType}
      </Button>
    );
  };

  return (
    <div className="px-2">
      <Card className="relative mx-auto">
        {connectingWallet ? (
          <div className="flex flex-col h-[258px] items-center justify-center">
            <h4 className="text-heading4 text-white font-semibold mb-2">
              Connecting to {connectingWallet} Wallet
            </h4>
            <ThreeDotLoader className="w-[78px]" />
          </div>
        ) : (
          <>
            <IconButton
              className="absolute top-2 right-2"
              onClick={onHideWalletConnection}
            >
              <Icon.Close className="text-white text-iconMedium" />
            </IconButton>
            <h4 className="text-heading4 text-white font-semibold mb-3">
              Connect {wallets.length === 0 ? '' : 'Second '}Wallet
            </h4>
            <StackView direction="vertical" spacing={2}>
              {Object.values(WalletType)
                .filter(filterWalletType)
                .map(renderWalletButton)}
            </StackView>
          </>
        )}
      </Card>
    </div>
  );
};

export default ConnectWalletPopup;
